import { Feature, Geometry, Position } from "geojson";
import geojsonRbush, { RBush } from "geojson-rbush";

export interface ValidationResult {
  featureId: string;
  valid: boolean;
  errors: string[];
}

export type ValidationFunction = (feature: Feature, allFeatures: Feature[], helpers: ValidationHelpers) => ValidationResult;

export interface ValidationHelpers {
  /** Find all features colocated at a specific position */
  findFeaturesAtPosition: (position: Position, layerId?: string, tolerance?: number) => Feature[];

  /** Find all features colocated with any vertex of a feature */
  findFeaturesAtFeatureVertices: (feature: Feature, layerId?: string, tolerance?: number) => Feature[];

  /** Check if two positions are colocated within tolerance */
  isColocated: (pos1: Position, pos2: Position, tolerance?: number) => boolean;

  /** Get the spatial index for custom queries */
  getSpatialIndex: () => RBush<Geometry, any>;

  /** Get all features by layer ID */
  getFeaturesByLayerId: (layerId: string) => Feature[];
}

/**
 * Generic validator that provides spatial indexing and helper methods
 * for layer-specific validation functions.
 *
 * Unlike BidirectionalValidator, this doesn't enforce specific rules.
 * Instead, it provides tools that validation functions can use.
 */
export class GenericValidator {
  private spatialIndex: RBush<Geometry, any>;
  private featureMap: Map<string, Feature>;
  private featuresByLayerId: Map<string, Set<string>>;
  private tolerance: number;

  constructor(features: Feature[], tolerance = 0.000001) {
    this.tolerance = tolerance;
    this.featureMap = new Map();
    this.featuresByLayerId = new Map();
    this.spatialIndex = geojsonRbush();

    this.setFeatures(features);
  }

  /**
   * Check if a feature has valid geometry for spatial indexing
   */
  private isValidGeometry(feature: Feature): boolean {
    if (!feature.geometry) return false;

    const geom = feature.geometry;

    // Check if geometry has coordinates
    if (!("coordinates" in geom)) return false;

    const coords = geom.coordinates as any;

    // Check if coordinates is not empty
    if (geom.type === "Point") {
      return Array.isArray(coords) && coords.length === 2;
    } else if (geom.type === "LineString") {
      return Array.isArray(coords) && coords.length >= 2;
    } else if (geom.type === "Polygon") {
      return Array.isArray(coords) && coords.length > 0 && coords[0].length >= 4;
    }

    return true; // For other geometry types, assume valid
  }

  /**
   * Update the validator with a new set of features
   */
  setFeatures(features: Feature[]) {
    this.featureMap.clear();
    this.featuresByLayerId.clear();

    // Build feature map and layer index
    features.forEach((feature) => {
      if (feature.id) {
        this.featureMap.set(feature.id as string, feature);

        const layerId = feature.properties?.layerId;
        if (layerId) {
          if (!this.featuresByLayerId.has(layerId)) {
            this.featuresByLayerId.set(layerId, new Set());
          }
          this.featuresByLayerId.get(layerId)!.add(feature.id as string);
        }
      }
    });

    // Rebuild spatial index - only with valid features
    this.spatialIndex.clear();
    if (features.length > 0) {
      const validFeatures = features.filter((f) => f.id !== undefined && this.isValidGeometry(f));
      if (validFeatures.length > 0) {
        this.spatialIndex.load(validFeatures);
      }
    }
  }

  /**
   * Update a single feature in the validator
   */
  updateFeature(feature: Feature) {
    const featureId = feature.id as string;

    // Update feature map
    this.featureMap.set(featureId, feature);

    // Update layer index
    const layerId = feature.properties?.layerId;
    if (layerId) {
      if (!this.featuresByLayerId.has(layerId)) {
        this.featuresByLayerId.set(layerId, new Set());
      }
      this.featuresByLayerId.get(layerId)!.add(featureId);
    }

    // Update spatial index
    this.spatialIndex.remove(feature, (a, b) => a.id === b.id);
    this.spatialIndex.insert(feature);
  }

  /**
   * Remove a feature from the validator
   */
  removeFeature(feature: Feature) {
    const featureId = feature.id as string;

    // Remove from feature map
    this.featureMap.delete(featureId);

    // Remove from layer index
    const layerId = feature.properties?.layerId;
    if (layerId) {
      this.featuresByLayerId.get(layerId)?.delete(featureId);
    }

    // Remove from spatial index
    this.spatialIndex.remove(feature, (a, b) => a.id === b.id);
  }

  /**
   * Find all features at a specific position
   */
  findFeaturesAtPosition(position: Position, layerId?: string, tolerance?: number): Feature[] {
    const tol = tolerance ?? this.tolerance;
    const [lng, lat] = position;

    // geojson-rbush expects bbox as [minX, minY, maxX, maxY]
    const bbox = {
      type: "Feature" as const,
      properties: {},
      geometry: {
        type: "Polygon" as const,
        coordinates: [
          [
            [lng - tol, lat - tol],
            [lng + tol, lat - tol],
            [lng + tol, lat + tol],
            [lng - tol, lat + tol],
            [lng - tol, lat - tol],
          ],
        ],
      },
    };

    const nearby = this.spatialIndex.search(bbox).features as Feature[];
    const filtered = nearby.filter((f) => {
      // Filter by layerId if specified
      if (layerId && f.properties?.layerId !== layerId) {
        return false;
      }

      // Check if feature is actually colocated at this position
      if (f.geometry.type === "Point") {
        const [fLng, fLat] = f.geometry.coordinates;
        const lngDist = Math.abs(fLng - lng);
        const latDist = Math.abs(fLat - lat);
        const isColocated = lngDist < tol && latDist < tol;
        return isColocated;
      } else if (f.geometry.type === "LineString") {
        // Check if any vertex is colocated
        const vertices = f.geometry.coordinates;
        for (let i = 0; i < vertices.length; i++) {
          const [fLng, fLat] = vertices[i];
          const lngDist = Math.abs(fLng - lng);
          const latDist = Math.abs(fLat - lat);
          const isColocated = lngDist < tol && latDist < tol;
          if (isColocated) return true;
        }
        return false;
      } else if (f.geometry.type === "Polygon") {
        // Check if any vertex in any ring is colocated
        return f.geometry.coordinates.some((ring) => ring.some(([fLng, fLat]) => Math.abs(fLng - lng) < tol && Math.abs(fLat - lat) < tol));
      }

      return false;
    });

    return filtered;
  }

  /**
   * Find all features colocated with any vertex of the given feature
   */
  findFeaturesAtFeatureVertices(feature: Feature, layerId?: string, tolerance?: number): Feature[] {
    const vertices = this.extractVertices(feature);
    const colocatedFeatures = new Map<string, Feature>();

    vertices.forEach((vertex) => {
      const features = this.findFeaturesAtPosition(vertex, layerId, tolerance);
      features.forEach((f) => {
        if (f.id && f.id !== feature.id) {
          colocatedFeatures.set(f.id as string, f);
        }
      });
    });

    return Array.from(colocatedFeatures.values());
  }

  /**
   * Check if two positions are colocated
   */
  isColocated(pos1: Position, pos2: Position, tolerance?: number): boolean {
    const tol = tolerance ?? this.tolerance;
    return Math.abs(pos1[0] - pos2[0]) < tol && Math.abs(pos1[1] - pos2[1]) < tol;
  }

  /**
   * Extract all vertices from a feature
   */
  private extractVertices(feature: Feature): Position[] {
    if (feature.geometry.type === "Point") {
      return [feature.geometry.coordinates];
    } else if (feature.geometry.type === "LineString") {
      return feature.geometry.coordinates;
    } else if (feature.geometry.type === "Polygon") {
      return feature.geometry.coordinates.flat();
    } else if (feature.geometry.type === "MultiPoint") {
      return feature.geometry.coordinates;
    } else if (feature.geometry.type === "MultiLineString") {
      return feature.geometry.coordinates.flat();
    } else if (feature.geometry.type === "MultiPolygon") {
      return feature.geometry.coordinates.flat(2);
    }
    return [];
  }

  /**
   * Get all features for a specific layer
   */
  getFeaturesByLayerId(layerId: string): Feature[] {
    const featureIds = this.featuresByLayerId.get(layerId);
    if (!featureIds) return [];

    return Array.from(featureIds)
      .map((id) => this.featureMap.get(id))
      .filter((f): f is Feature => f !== undefined);
  }

  /**
   * Get the spatial index for custom queries
   */
  getSpatialIndex(): RBush<Geometry, any> {
    return this.spatialIndex;
  }

  /**
   * Get validation helpers object for use in validation functions
   */
  getHelpers(): ValidationHelpers {
    return {
      findFeaturesAtPosition: this.findFeaturesAtPosition.bind(this),
      findFeaturesAtFeatureVertices: this.findFeaturesAtFeatureVertices.bind(this),
      isColocated: this.isColocated.bind(this),
      getSpatialIndex: this.getSpatialIndex.bind(this),
      getFeaturesByLayerId: this.getFeaturesByLayerId.bind(this),
    };
  }

  /**
   * Validate a feature using the provided validation function
   */
  validate(feature: Feature, validationFn: ValidationFunction, allFeatures?: Feature[]): ValidationResult {
    const features = allFeatures || Array.from(this.featureMap.values());
    return validationFn(feature, features, this.getHelpers());
  }

  /**
   * Validate a feature and all features colocated with it.
   * Returns validation results for all affected features.
   */
  validateWithColocated(feature: Feature, validationFunctions: Map<string, ValidationFunction>, allFeatures?: Feature[]): ValidationResult[] {
    const results: ValidationResult[] = [];
    const features = allFeatures || Array.from(this.featureMap.values());

    // Validate the feature itself
    const layerId = feature.properties?.layerId;
    if (layerId && validationFunctions.has(layerId)) {
      const validationFn = validationFunctions.get(layerId)!;
      results.push(this.validate(feature, validationFn, features));
    }

    // Find all colocated features
    const colocated = this.findFeaturesAtFeatureVertices(feature);

    // Validate each colocated feature
    colocated.forEach((colocatedFeature) => {
      const colocatedLayerId = colocatedFeature.properties?.layerId;
      if (colocatedLayerId && validationFunctions.has(colocatedLayerId)) {
        const validationFn = validationFunctions.get(colocatedLayerId)!;
        results.push(this.validate(colocatedFeature, validationFn, features));
      }
    });

    return results;
  }
}
